import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Fab,
  Badge,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  Business,
  LocalOffer,
  Analytics,
  Settings,
  Logout,
  Add,
  Edit,
  Visibility,
  Delete,
  TrendingUp,
  People,
  Schedule,
  NotificationsActive,
  CheckCircle,
  Warning,
  Info,
  ErrorOutline,
  Refresh,
  Launch,
  Share,
  Replay,
  HourglassTop,
  CreditCard
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { collection, getDocs, query, where, orderBy, limit, doc, updateDoc, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import {VenueData, OfferData, VenueApprovalRequest, DashboardStats} from '../types/VenueAdminTypes';
import SubscriptionManagement from '../components/venue-admin/SubscriptionManagement';
import EditOffer from '../components/venue-admin/EditOffer';
import VenueAdminAnalytics from '../components/analytics/VenueAdminAnalytics';
import VenueIoTAnalytics from '../components/analytics/Venueiotanalytics';
import VenueAdminCapacityAnalytics from '../components/analytics/VenueAdminCapacityAnalytics';
import SettingsComponent from '../components/shared/Settings';
import VenueImageManager from '../components/venue-admin/VenueImageManager';

const VenueAdminDashboard: React.FC = () => {
  const { signOut, venueAdminData, refreshUserRole } = useAuth();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [venues, setVenues] = useState<VenueData[]>([]);
  const [offers, setOffers] = useState<OfferData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState<VenueApprovalRequest[]>([]);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  
  // Stats state
  const [stats, setStats] = useState<DashboardStats>({
    totalVenues: 0,
    totalOffers: 0,
    activeOffers: 0,
    totalViews: 0,
    totalRedemptions: 0,
    monthlyViews: 0,
    monthlyRedemptions: 0,
    //averageRating: 0,
    subscription: {
      venueUsage: 0,
      offerUsage: 0,
      planName: '',
      status: '',
      daysUntilRenewal: 0,
    },
  });

  // Dialog states
  const [editVenueDialog, setEditVenueDialog] = useState<VenueData | null>(null);
  const [venueEditSaving, setVenueEditSaving] = useState<boolean>(false);
  const [venueEditForm, setVenueEditForm] = useState<{
    description: string;
    website: string;
    phone: string;
    maxCapacity: string;
  }>({ description: '', website: '', phone: '', maxCapacity: '' });
  const [editOfferDialog, setEditOfferDialog] = useState<OfferData | null>(null);
  const [reactivateOfferDialog, setReactivateOfferDialog] = useState<OfferData | null>(null);
  const [reactivateForm, setReactivateForm] = useState({ validFrom: '', validUntil: '' });
  const [reactivateSaving, setReactivateSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ type: 'venue' | 'offer'; item: VenueData | OfferData } | null>(null);

  // Team member state
  interface TeamMember {
    id: string;
    memberId: string;
    name: string;
    email: string;
    role: 'manager' | 'staff';
    status: string;
    createdAt: any;
  }
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showCreateMemberDialog, setShowCreateMemberDialog] = useState(false);
  const [deleteMemberDialog, setDeleteMemberDialog] = useState<TeamMember | null>(null);
  const [memberForm, setMemberForm] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'staff' });
  const [memberFormError, setMemberFormError] = useState('');
  const [memberSaving, setMemberSaving] = useState(false);

  // Load venue admin data
  useEffect(() => {
    if (venueAdminData?.adminId) {
      loadVenueAdminData();
    }
  }, [venueAdminData]);

  // Load team members when settings tab becomes active
  const settingsTabIndex = venueAdminData?.isTeamMember ? 5 : 6;
  useEffect(() => {
    if (currentTab === settingsTabIndex && venueAdminData?.adminId) {
      loadTeamMembers();
    }
  }, [currentTab, settingsTabIndex]);

  const loadVenueAdminData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

      if (!venueAdminData?.adminId) {
        throw new Error('No admin ID found');
      }

      console.log('🔄 Loading dashboard data for admin:', venueAdminData.adminId);

      // Load approval requests first
      const approvalRequestsQuery = query(
        collection(db, 'venue_approval_requests'),
        where('adminId', '==', venueAdminData.adminId),
        where('status', '==', 'pending'),
        orderBy('submittedAt', 'desc')
      );
      const approvalRequestsSnapshot = await getDocs(approvalRequestsQuery);
      const approvalRequestsData = approvalRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as VenueApprovalRequest));

      setPendingRequests(approvalRequestsData);
      console.log('📋 Pending requests:', approvalRequestsData.length);

      // Load venues owned by this admin
      const venuesQuery = query(
        collection(db, 'venues'),
        where('ownerId', '==', venueAdminData.adminId),
        //orderBy('createdAt', 'desc')
      );
      const venuesSnapshot = await getDocs(venuesQuery);
      const venuesData = venuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as VenueData));

      console.log('🏪 Venues loaded:', venuesData.length);

      // ✨ AUTO-SYNC VENUE COUNT IF MISMATCH DETECTED
      const actualVenueCount = venuesData.length;
      if (venueAdminData.venueCount !== actualVenueCount) {
        console.log(`⚠️ Venue count mismatch detected: DB has ${venueAdminData.venueCount}, actual is ${actualVenueCount}`);
        
        try {
          const venueAdminQuery = query(
            collection(db, 'venue_admins'),
            where('adminId', '==', venueAdminData.adminId)
          );
          const adminSnapshot = await getDocs(venueAdminQuery);

          if (!adminSnapshot.empty) {
            const adminDoc = adminSnapshot.docs[0];
            await updateDoc(doc(db, 'venue_admins', adminDoc.id), {
              venueCount: actualVenueCount,
              updatedAt: Timestamp.now(),
            });
            console.log(`✅ Auto-synced venue count to ${actualVenueCount}`);
            
            // Update local venueAdminData reference for immediate UI update
            if (venueAdminData) {
              (venueAdminData as any).venueCount = actualVenueCount;
            }
          }
        } catch (syncError) {
          console.error('❌ Failed to sync venue count:', syncError);
          // Don't throw - continue loading even if sync fails
        }
      }

      // Load offers for all venues owned by this admin
      const offersQuery = query(
        collection(db, 'offers'),
        where('ownerId', '==', venueAdminData.adminId),
        orderBy('createdAt', 'desc')
      );
      const offersSnapshot = await getDocs(offersQuery);
      const offersData = offersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OfferData));

      console.log('🎯 Offers loaded:', offersData.length);
      
      // Log offer details for debugging
      offersData.forEach(offer => {
        console.log(`Offer: ${offer.title} - Views: ${offer.analytics?.views || 0}, Redemptions: ${offer.analytics?.redemptions || 0}, Current: ${offer.currentRedemptions || 0}`);
      });

      // Calculate comprehensive stats with CORRECTED logic
      const totalVenues = venuesData.length;
      const pendingVenues = approvalRequestsData.length;
      const totalOffers = offersData.length;
      const activeOffers = offersData.filter(offer => 
        offer.isActive && 
        offer.status === 'active' && 
        new Date(offer.validUntil.toDate()) > new Date()
      ).length;

      // FIXED: Use correct fields for analytics
      const totalViews = offersData.reduce((sum, offer) => {
        const views = offer.analytics?.views || 0;
        console.log(`${offer.title}: ${views} views`);
        return sum + views;
      }, 0);

      // FIXED: Use both analytics.redemptions AND currentRedemptions for total count
      const totalRedemptions = offersData.reduce((sum, offer) => {
        const analyticsRedemptions = offer.analytics?.redemptions || 0;
        const currentRedemptions = offer.currentRedemptions || 0;
        // Use the higher of the two (they should be the same, but fallback logic)
        const redemptions = Math.max(analyticsRedemptions, currentRedemptions);
        console.log(`${offer.title}: ${redemptions} redemptions (analytics: ${analyticsRedemptions}, current: ${currentRedemptions})`);
        return sum + redemptions;
      }, 0);
      
      // FIXED: Calculate monthly stats properly (use actual data, not approximations)
      // For now, use total stats as monthly (you can implement date filtering later)
      const monthlyViews = totalViews; // In production, filter by current month
      const monthlyRedemptions = totalRedemptions; // In production, filter by current month

      console.log('📊 Calculated stats:', {
        totalViews,
        totalRedemptions,
        monthlyViews,
        monthlyRedemptions
      });

      // FIXED: Subscription stats using correct field names
      const subscription = venueAdminData.subscription;
      const currentOffersUsed = activeOffers;
      const maxOffersAllowed = subscription?.totalOffersLimit || 1;
      
      console.log('💳 Subscription data:', {
        currentOffersUsed, 
        maxOffersAllowed,  
        activeOffers,      
        totalOffers,       
        planId: subscription?.planId
      });

      // Calculate renewal days
      const daysUntilRenewal = subscription?.currentPeriodEnd ? 
        Math.ceil((subscription.currentPeriodEnd.toDate().getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

      // FIXED: Set stats with corrected calculations using actualVenueCount
      setStats({
        totalVenues: totalVenues + pendingVenues,
        totalOffers,
        activeOffers,
        totalViews,
        totalRedemptions,
        monthlyViews,
        monthlyRedemptions,
        subscription: {
          venueUsage: venueAdminData.venueLimit > 0 ? (actualVenueCount / venueAdminData.venueLimit) * 100 : 0,
          offerUsage: maxOffersAllowed > 0 ? (currentOffersUsed / maxOffersAllowed) * 100 : 0,
          planName: subscription?.planId?.replace('_monthly', '')?.toUpperCase() || 'NONE',
          status: subscription?.status || 'inactive',
          daysUntilRenewal,
        },
      });

      setVenues(venuesData);
      setOffers(offersData);

      console.log('✅ Dashboard data loaded successfully');

    } catch (error: any) {
      console.error('❌ Error loading venue admin data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueAdminData]);

  const handleRefresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    await loadVenueAdminData();
    setRefreshing(false);
    setSuccess('Dashboard refreshed successfully');
  }, [loadVenueAdminData]);

  // Load team members from Firestore
  const loadTeamMembers = useCallback(async () => {
    if (!venueAdminData?.adminId) return;
    try {
      setTeamLoading(true);
      const snap = await getDocs(query(
        collection(db, 'venue_team_members'),
        where('ownerAdminId', '==', venueAdminData.adminId),
        orderBy('createdAt', 'desc')
      ));
      setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() } as TeamMember)));
    } catch (err) {
      console.error('Error loading team members:', err);
    } finally {
      setTeamLoading(false);
    }
  }, [venueAdminData]);

  const handleCreateMember = useCallback(async () => {
    setMemberFormError('');
    if (!memberForm.name.trim() || !memberForm.email.trim() || !memberForm.password) {
      setMemberFormError('Name, email and password are required.');
      return;
    }
    if (memberForm.password.length < 8) {
      setMemberFormError('Password must be at least 8 characters.');
      return;
    }
    if (memberForm.password !== memberForm.confirmPassword) {
      setMemberFormError('Passwords do not match.');
      return;
    }
    try {
      setMemberSaving(true);
      const createFn = httpsCallable(functions, 'createVenueTeamMember');
      await createFn({ name: memberForm.name.trim(), email: memberForm.email.trim(), password: memberForm.password, role: memberForm.role });
      setSuccess(`Team member ${memberForm.name} added successfully`);
      setShowCreateMemberDialog(false);
      setMemberForm({ name: '', email: '', password: '', confirmPassword: '', role: 'staff' });
      await loadTeamMembers();
    } catch (err: any) {
      setMemberFormError(err.message || 'Failed to create team member. Please try again.');
    } finally {
      setMemberSaving(false);
    }
  }, [memberForm, loadTeamMembers]);

  const handleDeleteMember = useCallback(async () => {
    if (!deleteMemberDialog) return;
    try {
      setMemberSaving(true);
      const deleteFn = httpsCallable(functions, 'deleteVenueTeamMember');
      await deleteFn({ memberId: deleteMemberDialog.memberId });
      setSuccess(`${deleteMemberDialog.name} removed from your team`);
      setDeleteMemberDialog(null);
      await loadTeamMembers();
    } catch (err: any) {
      setError(err.message || 'Failed to remove team member.');
    } finally {
      setMemberSaving(false);
    }
  }, [deleteMemberDialog, loadTeamMembers]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const getStatusColor = (status: string): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'active':
      case 'verified':
        return 'success';
      case 'pending':
      case 'draft':
      case 'scheduled':
        return 'warning';
      case 'suspended':
      case 'expired':
        return 'error';
      case 'paused':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (status) {
      case 'active':
      case 'verified':
        return <CheckCircle />;
      case 'pending':
      case 'draft':
      case 'scheduled':
        return <Schedule />;
      case 'suspended':
      case 'expired':
        return <ErrorOutline />;
      case 'paused':
        return <Info />;
      default:
        return <Warning />;
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp.toDate()).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  // Polished Dashboard Overview
  const DashboardOverview = (): React.ReactElement => {
    const conversionRate = stats.monthlyViews > 0
      ? ((stats.monthlyRedemptions / stats.monthlyViews) * 100).toFixed(1)
      : '0.0';

    const statCards = [
      {
        label: 'My Venues',
        value: stats.totalVenues,
        sub: `${venues.length} / ${venueAdminData?.venueLimit || 0} slots used`,
        icon: <Business />,
        accent: '#6c63ff',
        bgTint: '#f5f4ff',
        alerts: (
          <>
            {venueAdminData?.subscription?.scheduledPlanChange && (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.75rem' }}>
                Plan changes to <strong>{venueAdminData.subscription.scheduledPlanChange.newPlanId.replace('_monthly', '').toUpperCase()}</strong> on{' '}
                {new Date(venueAdminData.subscription.scheduledPlanChange.scheduledFor * 1000).toLocaleDateString()}
              </Alert>
            )}
            {venueAdminData?.subscription?.cancelAtPeriodEnd && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.75rem' }}
                action={<Button size="small" onClick={() => setShowSubscriptionDialog(true)}>Reactivate</Button>}>
                Subscription ends {formatDate(venueAdminData.subscription.cancelEffectiveDate)}
              </Alert>
            )}
          </>
        ),
      },
      {
        label: 'Active Offers',
        value: stats.activeOffers,
        sub: `${stats.totalOffers} total offers`,
        icon: <LocalOffer />,
        accent: '#16a76a',
        bgTint: '#f0faf5',
      },
      {
        label: 'Monthly Views',
        value: stats.monthlyViews.toLocaleString(),
        sub: `${stats.totalViews.toLocaleString()} all-time`,
        icon: <TrendingUp />,
        accent: '#f59e0b',
        bgTint: '#fffbf0',
      },
      {
        label: 'Redemptions',
        value: stats.monthlyRedemptions,
        sub: `${conversionRate}% conversion`,
        icon: <People />,
        accent: '#e040fb',
        bgTint: '#fdf4ff',
      },
    ];

    return (
      <Box sx={{ background: '#f8f9fc', minHeight: '100%', p: 0 }}>
        {/* Stat Cards Row */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 6, md: 3 }}>
              {card.alerts}
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: 2.5,
                  p: 2.5,
                  border: '1px solid #e8eaf0',
                  borderLeft: `4px solid ${card.accent}`,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 20px rgba(0,0,0,0.09)` },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 0.75 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1, mb: 0.5 }}>
                      {card.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#8a94a6' }}>
                      {card.sub}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: card.bgTint, color: card.accent, '& svg': { fontSize: 20 },
                  }}>
                    {card.icon}
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Middle row: Subscription + Quick Actions */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>

          {/* Subscription & Usage */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: '100%', borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="#1a1d23">Subscription & Usage</Typography>
                    <Typography variant="caption" color="text.secondary">{stats.subscription.planName} plan · renews in {stats.subscription.daysUntilRenewal} days</Typography>
                  </Box>
                  <Chip
                    label={stats.subscription.status.toUpperCase()}
                    color={getStatusColor(stats.subscription.status)}
                    icon={getStatusIcon(stats.subscription.status)}
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box mb={2.5}>
                      <Box display="flex" justifyContent="space-between" mb={0.75}>
                        <Typography variant="body2" color="text.secondary">Venues</Typography>
                        <Typography variant="body2" fontWeight={600}>{venues.length} / {venueAdminData?.venueLimit || 0}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: '#eef0f5', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 3,
                          width: `${Math.min(stats.subscription.venueUsage, 100)}%`,
                          background: stats.subscription.venueUsage > 80 ? '#ef4444' : '#6c63ff',
                          transition: 'width 0.6s ease',
                        }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={0.75}>
                        <Typography variant="body2" color="text.secondary">Active Offers</Typography>
                        <Typography variant="body2" fontWeight={600}>{stats.activeOffers} / {venueAdminData?.subscription?.totalOffersLimit || 0}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: '#eef0f5', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 3,
                          width: `${Math.min(stats.subscription.offerUsage, 100)}%`,
                          background: stats.subscription.offerUsage > 80 ? '#ef4444' : '#16a76a',
                          transition: 'width 0.6s ease',
                        }} />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ background: '#f8f9fc', borderRadius: 2, p: 2, border: '1px solid #e8eaf0' }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, color: '#8a94a6' }}>
                        This Month
                      </Typography>
                      <Box display="flex" gap={2.5} mt={1.5} flexWrap="wrap">
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="#1a1d23">{stats.monthlyViews.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">Views</Typography>
                        </Box>
                        <Box sx={{ width: '1px', background: '#e8eaf0', my: 0.5 }} />
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="#1a1d23">{stats.monthlyRedemptions}</Typography>
                          <Typography variant="caption" color="text.secondary">Redemptions</Typography>
                        </Box>
                        <Box sx={{ width: '1px', background: '#e8eaf0', my: 0.5 }} />
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="#f59e0b">{conversionRate}%</Typography>
                          <Typography variant="caption" color="text.secondary">Conversion</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={2} color="#1a1d23">Quick Actions</Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {[
                    { label: 'Add New Venue', icon: <Business sx={{ fontSize: 17 }} />, primary: true, disabled: venues.length >= (venueAdminData?.venueLimit ?? 0), onClick: () => setCurrentTab(1) },
                    { label: 'Create Offer', icon: <LocalOffer sx={{ fontSize: 17 }} />, primary: true, disabled: !venues.length || stats.activeOffers >= (venueAdminData?.subscription?.totalOffersLimit || 0), onClick: () => navigate('/create-offer') },
                    { label: 'View Analytics', icon: <Analytics sx={{ fontSize: 17 }} />, primary: false, onClick: () => setCurrentTab(3) },
                    { label: 'Manage Subscription', icon: <CreditCard sx={{ fontSize: 17 }} />, primary: false, onClick: () => setShowSubscriptionDialog(true) },
                  ].map((action) => (
                    <Button
                      key={action.label}
                      fullWidth
                      variant={action.primary ? 'contained' : 'outlined'}
                      startIcon={action.icon}
                      disabled={action.disabled}
                      onClick={action.onClick}
                      sx={{
                        justifyContent: 'flex-start',
                        borderRadius: 1.5,
                        py: 0.9,
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        ...(action.primary ? { boxShadow: 'none', '&:hover': { boxShadow: 'none' } } : {
                          borderColor: '#e0e3ea',
                          color: '#4a5568',
                          '&:hover': { borderColor: '#c0c6d4', background: '#f8f9fc' },
                        }),
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                  <Button
                    fullWidth variant="text"
                    startIcon={<Refresh sx={{ fontSize: 17 }} />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{ justifyContent: 'flex-start', borderRadius: 1.5, py: 0.9, fontSize: '0.82rem', color: '#8a94a6' }}
                  >
                    {refreshing ? 'Refreshing...' : 'Refresh Data'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom row: Venues + Offers mini-cards */}
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Typography variant="h6" fontWeight={600} color="#1a1d23">My Venues</Typography>
                  <Button size="small" variant="text" sx={{ fontSize: '0.75rem', color: '#6c63ff', fontWeight: 600 }} onClick={() => setCurrentTab(1)}>
                    View all →
                  </Button>
                </Box>
                {venues.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {venues.slice(0, 3).map((venue) => (
                      <Box key={venue.id} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1.5, borderRadius: 2, background: '#f8f9fc',
                        border: '1px solid #eef0f5',
                      }}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 34, height: 34, fontSize: '0.85rem', background: '#f0eeff', color: '#6c63ff', border: '1px solid #e0dbff' }}>
                            {(venue.name || venue.businessName)?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160, color: '#1a1d23' }}>
                              {venue.name || venue.businessName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                              {venue.address || 'No address'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary">{venue.analytics?.monthlyViews || 0} views</Typography>
                          <Chip label={venue.status} color={getStatusColor(venue.status)} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Business sx={{ fontSize: 40, color: '#d1d5e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No venues yet</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Typography variant="h6" fontWeight={600} color="#1a1d23">Recent Offers</Typography>
                  <Button size="small" variant="text" sx={{ fontSize: '0.75rem', color: '#16a76a', fontWeight: 600 }} onClick={() => setCurrentTab(2)}>
                    View all →
                  </Button>
                </Box>
                {offers.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {offers.slice(0, 3).map((offer) => (
                      <Box key={offer.id} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1.5, borderRadius: 2, background: '#f8f9fc',
                        border: '1px solid #eef0f5',
                      }}>
                        <Box>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 170, color: '#1a1d23' }}>{offer.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{offer.venueName}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary">{offer.analytics?.redemptions || 0} used</Typography>
                          <Chip label={offer.status} color={getStatusColor(offer.status)} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <LocalOffer sx={{ fontSize: 40, color: '#d1d5e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No offers yet</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Enhanced My Venues Tab
  const MyVenuesTab = (): React.ReactElement => (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">My Venues</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={(venues.length + pendingRequests.length) >= venueAdminData?.venueLimit}
              onClick={() => navigate('/create-venue')}
            >
              Add Venue
            </Button>
          </Box>

          {/* Pending Requests Section */}
          {pendingRequests.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Pending Approval ({pendingRequests.length})
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                These venues are waiting for JamApp admin approval. You'll be notified once they're reviewed.
              </Alert>
              
              <TableContainer sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business Details</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRequests.map((request) => (
                      <TableRow key={request.id} sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: 'warning.main' }}>
                              <HourglassTop />
                            </Avatar>
                            <div>
                              <Typography variant="body2" fontWeight="bold">
                                {request.businessName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Trading: {request.tradingName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {request.address}
                              </Typography>
                            </div>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.requestType.toUpperCase()}
                            color={request.requestType === 'claim' ? 'warning' : 'info'}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {request.businessType}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="PENDING APPROVAL"
                            color="warning"
                            size="small"
                            icon={<HourglassTop />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(request.submittedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Request">
                              <IconButton size="small" color="error">
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Active Venues Section */}
          <Typography variant="h6" gutterBottom>
            Active Venues ({venues.length})
          </Typography>
          
          {venues.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Business Details</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {venues.map((venue) => (
                    <TableRow key={venue.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2 }}>{(venue.name || venue.businessName)?.charAt(0)}</Avatar>
                          <div>
                            <Typography variant="body2" fontWeight="bold">
                              {venue.name || venue.businessName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {venue.email}
                            </Typography>
                            {venue.phone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {venue.phone}
                              </Typography>
                            )}
                          </div>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {venue.address || 'No address'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={venue.status}
                          color={getStatusColor(venue.status)}
                          size="small"
                          icon={getStatusIcon(venue.status)}
                        />
                      </TableCell>
                        <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {venue.analytics?.monthlyViews || 0} monthly views
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {}
                          </Typography>
                          <Box display="flex" alignItems="center" mt={0.5}>
                            <Typography variant="caption" sx={{ minWidth: 60 }}>
                              {offers.filter(o => 
                                o.venueId === venue.id && 
                                o.isActive && 
                                o.status === 'active' &&
                                new Date(o.validUntil?.toDate?.() ?? o.validUntil) > new Date()
                              ).length}/{venue.subscription?.offersLimit || venue.usage?.offersLimit || 0} offers
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(offers.filter(o => 
                                o.venueId === venue.id && 
                                o.isActive && 
                                o.status === 'active' &&
                                new Date(o.validUntil?.toDate?.() ?? o.validUntil) > new Date()
                              ).length / (venue.subscription?.offersLimit || venue.usage?.offersLimit || 1)) * 100}
                              sx={{ ml: 1, width: 60, height: 4 }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(venue.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Venue">
                            <IconButton size="small" onClick={() => {
                              setEditVenueDialog(venue);
                              setVenueEditForm({
                                description: venue.profile?.description || '',
                                website:     venue.profile?.website     || '',
                                phone:       venue.phone                || '',
                                maxCapacity: venue.maxCapacity != null ? String(venue.maxCapacity) : '',
                              });
                            }}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share">
                            <IconButton size="small">
                              <Share />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={8}>
              <Business color="disabled" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {pendingRequests.length > 0 ? 'No active venues yet' : 'No venues yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {pendingRequests.length > 0 ? 
                  'Your pending venues will appear here once approved.' :
                  'Create your first venue to start managing offers and engaging with customers.'
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                disabled={(venues.length + pendingRequests.length) >= venueAdminData?.venueLimit}
                onClick={() => navigate('/create-venue')}
              >
                Create First Venue
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Enhanced My Offers Tab
  const MyOffersTab = (): React.ReactElement => (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">My Offers</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={!venues.length || (stats.activeOffers >= (venueAdminData?.subscription?.totalOffersLimit || 0))}
              onClick={() => navigate('/create-offer')}
            >
              Create Offer
            </Button>
          </Box>
          
          {offers.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Offer Details</TableCell>
                    <TableCell>Venue</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Validity</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>
                        <div>
                          <Typography variant="body2" fontWeight="bold">
                            {offer.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {offer.description?.substring(0, 60)}...
                          </Typography>
                          <Box mt={0.5}>
                            <Chip
                              label={`${offer.discountValue}${offer.discountType === 'percentage' ? '%' : ''} OFF`}
                              size="small"
                              color="secondary"
                            />
                          </Box>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {offer.venueName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.status}
                          color={getStatusColor(offer.status)}
                          size="small"
                          icon={getStatusIcon(offer.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <Typography variant="body2">
                            Until {formatDate(offer.validUntil)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {offer.maxRedemptions ? `${offer.currentRedemptions}/${offer.maxRedemptions} used` : 'Unlimited'}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {offer.analytics?.views || 0} views
                          </Typography>
                          <Typography variant="body2">
                            {offer.analytics?.redemptions || 0} redemptions
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(offer.analytics?.views && offer.analytics?.redemptions) 
                              ? ((offer.analytics.redemptions / offer.analytics.views) * 100).toFixed(1) 
                              : 0}% conversion
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit Offer">
                            <IconButton size="small" onClick={() => setEditOfferDialog(offer)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          {offer.status === 'expired' && (
                            <Tooltip title="Reactivate Offer">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openReactivateDialog(offer)}
                              >
                                <Replay />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Offer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ type: 'offer', item: offer })}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={8}>
              <LocalOffer color="disabled" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No offers yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create your first offer to start attracting customers to your venues.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                disabled={!venues.length || (stats.activeOffers >= (venueAdminData?.subscription?.totalOffersLimit || 0))}
                onClick={() => navigate('/create-offer')}
              >
                Create First Offer
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Enhanced Analytics Tab
  const AnalyticsTab = (): React.ReactElement => (
    <VenueAdminAnalytics 
      adminId={venueAdminData?.adminId || ''}
      venues={venues}
      offers={offers}
      stats={{
        totalViews: stats.totalViews,
        totalRedemptions: stats.totalRedemptions,
        monthlyViews: stats.monthlyViews,
        monthlyRedemptions: stats.monthlyRedemptions,
      }}
    />
  );

  //subscription management
  const SubscriptionTab = (): React.ReactElement => (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subscription Management
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage your subscription plan, billing, and account settings
          </Typography>
          <Button
            variant="contained"
            startIcon={<Settings />}
            onClick={() => setShowSubscriptionDialog(true)}
          >
            Manage Subscription
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  // Capacity analytics tab
  const CapacityTab = (): React.ReactElement => {
    if (!venueAdminData?.adminId) {
      return (
        <Box>
          <Alert severity="error">Unable to load capacity data. Admin ID not found.</Alert>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={3}>
        {/* IoT sensor patron counts — one card per venue */}
        {venues.map((venue) => (
          <VenueIoTAnalytics
            key={venue.id}
            venueId={venue.id}
            venueName={venue.name || venue.businessName || 'Unnamed Venue'}
            venueAdminId={venueAdminData.adminId}
          />
        ))}

        {/* BestTime forecasted vs live busyness */}
        <VenueAdminCapacityAnalytics venueAdminData={venueAdminData} />
      </Box>
    );
  };

  // Settings tab
  const SettingsTab = (): React.ReactElement => {
    if (!venueAdminData?.adminId) {
      return (
        <Box>
          <Alert severity="error">Unable to load settings. Admin data not found.</Alert>
        </Box>
      );
    }

    return (
      <Box>
        {/* Existing Settings Component */}
        <SettingsComponent
          userType="venue_admin"
          userData={venueAdminData}
          onUpdate={loadVenueAdminData}
        />

        {/* Team Members Card */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6">Team Members</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add logins for staff who need access to your venue dashboard. Max 5 members.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                disabled={teamMembers.filter(m => m.status === 'active').length >= 5}
                onClick={() => {
                  setMemberForm({ name: '', email: '', password: '', confirmPassword: '', role: 'staff' });
                  setMemberFormError('');
                  setShowCreateMemberDialog(true);
                }}
              >
                Add Member
              </Button>
            </Box>

            {teamLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={32} />
              </Box>
            ) : teamMembers.length === 0 ? (
              <Box textAlign="center" py={4}>
                <People color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No team members yet. Add staff who need dashboard access.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                              {member.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2">{member.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{member.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.role}
                            size="small"
                            color={member.role === 'manager' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.status}
                            size="small"
                            color={getStatusColor(member.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(member.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove member">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteMemberDialog(member)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Team members can log in and manage your venues and offers. They cannot change subscription or billing settings.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Save venue edits
  const handleSaveVenue = useCallback(async (): Promise<void> => {
    if (!editVenueDialog) return;
    try {
      setVenueEditSaving(true);
      const capacityNum = venueEditForm.maxCapacity.trim()
        ? parseInt(venueEditForm.maxCapacity, 10)
        : null;
      await updateDoc(doc(db, 'venues', editVenueDialog.id), {
        phone:                       venueEditForm.phone.trim()       || null,
        maxCapacity:                 isNaN(capacityNum!) ? null : capacityNum,
        'profile.description':       venueEditForm.description.trim() || null,
        'profile.website':           venueEditForm.website.trim()     || null,
        updatedAt:                   Timestamp.now(),
      });
      setSuccess('Venue updated successfully');
      setEditVenueDialog(null);
      await loadVenueAdminData();
    } catch (err: any) {
      console.error('Error saving venue:', err);
      setError('Failed to save venue. Please try again.');
    } finally {
      setVenueEditSaving(false);
    }
  }, [editVenueDialog, venueEditForm, loadVenueAdminData]);

  // Delete offer handler
  const handleDeleteOffer = useCallback(async () => {
    if (!deleteDialog || deleteDialog.type !== 'offer') return;
    const offer = deleteDialog.item as OfferData;
    try {
      // Use Cloud Function so delete + counter decrement happen atomically
      const deleteOfferFn = httpsCallable(functions, 'deleteOffer');
      await deleteOfferFn({ offerId: offer.id });

      setSuccess('Offer deleted successfully');
      setDeleteDialog(null);
      await refreshUserRole();   // re-fetch venueAdminData so totalOffersUsed updates
      await loadVenueAdminData();
    } catch (err: any) {
      console.error('Error deleting offer:', err);
      setError('Failed to delete offer. Please try again.');
    }
  }, [deleteDialog, refreshUserRole, loadVenueAdminData]);

  // Local-date helper — avoids the UTC rollback bug from toISOString()
  const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const openReactivateDialog = useCallback((offer: OfferData) => {
    const today = toLocalDateString(new Date());
    const thirtyDaysOut = toLocalDateString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    setReactivateForm({ validFrom: today, validUntil: thirtyDaysOut });
    setReactivateOfferDialog(offer);
  }, []);

  const handleReactivateOffer = useCallback(async () => {
    if (!reactivateOfferDialog) return;
    if (!reactivateForm.validFrom || !reactivateForm.validUntil) {
      setError('Please select both start and end dates');
      return;
    }
    if (new Date(reactivateForm.validFrom) >= new Date(reactivateForm.validUntil)) {
      setError('Start date must be before end date');
      return;
    }

    setReactivateSaving(true);
    setError('');
    try {
      const newValidFrom = new Date(reactivateForm.validFrom + 'T00:00:00');
      const newValidUntil = new Date(reactivateForm.validUntil + 'T23:59:59');
      const now = new Date();
      const isNowActive = newValidFrom <= now && newValidUntil > now;

      await updateDoc(doc(db, 'offers', reactivateOfferDialog.id), {
        validFrom: Timestamp.fromDate(newValidFrom),
        validUntil: Timestamp.fromDate(newValidUntil),
        isActive: true,
        status: isNowActive ? 'active' : 'scheduled',
        updatedAt: Timestamp.now(),
      });

      setSuccess('Offer reactivated successfully');
      setReactivateOfferDialog(null);
      await loadVenueAdminData();
    } catch (err: any) {
      console.error('Error reactivating offer:', err);
      setError('Failed to reactivate offer. Please try again.');
    } finally {
      setReactivateSaving(false);
    }
  }, [reactivateOfferDialog, reactivateForm, loadVenueAdminData]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8f9fc' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0} sx={{
        background: '#fff',
        borderBottom: '1px solid #e8eaf0',
      }}>
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #6c63ff, #e040fb)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Dashboard sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em', color: '#1a1d23' }}>
              JamApp <Typography component="span" sx={{ fontWeight: 400, color: '#8a94a6', fontSize: 'inherit' }}>Portal</Typography>
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2, color: '#1a1d23' }}>{venueAdminData?.name}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#8a94a6', lineHeight: 1.2 }}>{venueAdminData?.company}</Typography>
            </Box>
            <IconButton onClick={handleMenuClick} sx={{ p: 0.5 }}>
              <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg, #ede9ff, #fce4ff)', border: '1px solid #e0dbff', fontSize: '0.85rem', color: '#6c63ff' }}>
                {venueAdminData?.name?.charAt(0) || <AccountCircle />}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ sx: { mt: 1, minWidth: 140, borderRadius: 2, border: '1px solid #e8eaf0', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' } }}
          >
            <MenuItem onClick={signOut} sx={{ fontSize: '0.85rem', gap: 1 }}>
              <Logout fontSize="small" /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation Tabs */}
      <Box sx={{
        background: '#fff',
        borderBottom: '1px solid #e8eaf0',
        px: { xs: 1, md: 2 },
      }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48, fontSize: '0.78rem', fontWeight: 500, textTransform: 'none',
              letterSpacing: 0, px: 2, gap: 0.75, color: '#8a94a6',
              '&.Mui-selected': { color: '#1a1d23', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': { height: 2, borderRadius: '2px 2px 0 0', background: 'linear-gradient(90deg, #6c63ff, #e040fb)' },
          }}
        >
          <Tab label="Overview" icon={<Dashboard sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab
            label={<Badge badgeContent={venues.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>Venues</Badge>}
            icon={<Business sx={{ fontSize: 16 }} />} iconPosition="start"
          />
          <Tab
            label={<Badge badgeContent={stats.activeOffers} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>Offers</Badge>}
            icon={<LocalOffer sx={{ fontSize: 16 }} />} iconPosition="start"
          />
          <Tab label="Analytics" icon={<Analytics sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="Capacity" icon={<Schedule sx={{ fontSize: 16 }} />} iconPosition="start" />
          {!venueAdminData?.isTeamMember && <Tab label="Subscription" icon={<CreditCard sx={{ fontSize: 16 }} />} iconPosition="start" />}
          <Tab label="Settings" icon={<Settings sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Team member banner */}
      {venueAdminData?.isTeamMember && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          You are logged in as a <strong>{venueAdminData.teamMemberRole}</strong> team member. Some features are restricted to the account owner.
        </Alert>
      )}

      {/* Alerts */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        {/* Usage warnings */}
     {/*  {stats.subscription.venueUsage > 80 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Venue Limit Warning:</strong> You're using {Math.round(stats.subscription.venueUsage)}% of your venue limit. 
            Consider upgrading your plan to add more venues.
          </Alert>
        )} */}
        
        {stats.subscription.offerUsage > 80 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Offer Limit Warning:</strong> You're using {Math.round(stats.subscription.offerUsage)}% of your offer limit. 
            Consider upgrading your plan to create more offers.
          </Alert>
        )}
      </Container>

      {/* Content — tab indices shift by 1 when subscription tab hidden for team members */}
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        {currentTab === 0 && <DashboardOverview />}
        {currentTab === 1 && <MyVenuesTab />}
        {currentTab === 2 && <MyOffersTab />}
        {currentTab === 3 && <AnalyticsTab />}
        {currentTab === 4 && <CapacityTab />}
        {!venueAdminData?.isTeamMember && currentTab === 5 && <SubscriptionTab />}
        {(venueAdminData?.isTeamMember ? currentTab === 5 : currentTab === 6) && <SettingsTab />}
      </Container>

      {/* Floating Action Button for Quick Actions */}
      {currentTab === 0 && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => {
            if (venues.length === 0) {
              navigate('/create-venue');
            } else {
              navigate('/create-offer');
            }
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Edit Venue Dialog */}
      <Dialog
        open={!!editVenueDialog}
        onClose={() => setEditVenueDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Venue Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            Update your venue information. Changes will be reflected in your public venue profile.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={venueEditForm.phone}
              onChange={(e) => setVenueEditForm(prev => ({ ...prev, phone: e.target.value }))}
              disabled={venueEditSaving}
            />
            <TextField
              fullWidth
              label="Website"
              value={venueEditForm.website}
              onChange={(e) => setVenueEditForm(prev => ({ ...prev, website: e.target.value }))}
              disabled={venueEditSaving}
              placeholder="https://"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={venueEditForm.description}
              onChange={(e) => setVenueEditForm(prev => ({ ...prev, description: e.target.value }))}
              disabled={venueEditSaving}
              helperText="Tell customers about your venue"
            />
            <TextField
              fullWidth
              label="Max Capacity"
              type="number"
              value={venueEditForm.maxCapacity}
              onChange={(e) => setVenueEditForm(prev => ({ ...prev, maxCapacity: e.target.value }))}
              disabled={venueEditSaving}
              inputProps={{ min: 0 }}
              helperText="This will assist in venue capacity accuracy"
            />

            {/* Venue image manager */}
            {editVenueDialog && (
              <Box sx={{ mt: 2 }}>
                <VenueImageManager
                  venueId={editVenueDialog.id}
                  ownerId={editVenueDialog.ownerId}
                  existingImages={(editVenueDialog as any)?.profile?.images || []}
                  googlePhotoUrl={(editVenueDialog as any)?.photoUrl}
                  onImagesUpdated={(imgs) => {
                    setVenues(prev => prev.map(v =>
                      v.id === editVenueDialog.id
                        ? { ...v, profile: { ...v.profile, images: imgs } } as any
                        : v
                    ));
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditVenueDialog(null)} disabled={venueEditSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveVenue}
            disabled={venueEditSaving}
            startIcon={venueEditSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {venueEditSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Offer Dialog */}
      <EditOffer
        open={!!editOfferDialog}
        offer={editOfferDialog}
        onClose={() => setEditOfferDialog(null)}
        onSuccess={async () => {
          await loadVenueAdminData();
          setSuccess('Offer updated successfully');
          setEditOfferDialog(null);
        }}
      />

      {/* Reactivate Offer Dialog */}
      <Dialog
        open={!!reactivateOfferDialog}
        onClose={() => setReactivateOfferDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reactivate Offer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            "{reactivateOfferDialog?.title}" has expired. Choose new start and end dates to make it live again.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Valid From"
              type="date"
              value={reactivateForm.validFrom}
              onChange={(e) => setReactivateForm(prev => ({ ...prev, validFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              label="Valid Until"
              type="date"
              value={reactivateForm.validUntil}
              onChange={(e) => setReactivateForm(prev => ({ ...prev, validUntil: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReactivateOfferDialog(null)} disabled={reactivateSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReactivateOffer}
            disabled={reactivateSaving}
            startIcon={reactivateSaving ? <CircularProgress size={16} color="inherit" /> : <Replay />}
          >
            {reactivateSaving ? 'Reactivating...' : 'Reactivate Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete {deleteDialog?.type === 'venue' ? 'Venue' : 'Offer'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteDialog?.type}? This action cannot be undone.
          </Typography>
          {deleteDialog?.type === 'venue' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deleting this venue will also remove all associated offers and analytics data.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteOffer}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SubscriptionManagement
        open={showSubscriptionDialog}
        venueAdminData={venueAdminData!}
        onClose={() => setShowSubscriptionDialog(false)}
        onSuccess={async () => {
          await loadVenueAdminData();
          setSuccess('Subscription updated successfully');
        }}
      />

      {/* Create Team Member Dialog */}
      <Dialog
        open={showCreateMemberDialog}
        onClose={() => !memberSaving && setShowCreateMemberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            Team members can log in and manage your venues and offers. They cannot access billing or subscription settings.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                disabled={memberSaving}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                disabled={memberSaving}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={memberForm.role}
                  label="Role"
                  onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                  disabled={memberSaving}
                >
                  <MenuItem value="staff">Staff — view and redeem offers</MenuItem>
                  <MenuItem value="manager">Manager — full venue & offer management</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={memberForm.password}
                onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                helperText="Minimum 8 characters"
                disabled={memberSaving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={memberForm.confirmPassword}
                onChange={(e) => setMemberForm({ ...memberForm, confirmPassword: e.target.value })}
                disabled={memberSaving}
              />
            </Grid>
            {memberFormError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{memberFormError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateMemberDialog(false)} disabled={memberSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateMember}
            disabled={memberSaving || !memberForm.name || !memberForm.email || !memberForm.password}
            startIcon={memberSaving ? <CircularProgress size={16} color="inherit" /> : <Add />}
          >
            {memberSaving ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Member Dialog */}
      <Dialog
        open={!!deleteMemberDialog}
        onClose={() => !memberSaving && setDeleteMemberDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteMemberDialog?.name}</strong> from your team?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will immediately revoke their access. They will no longer be able to log in to the dashboard.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMemberDialog(null)} disabled={memberSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMember}
            disabled={memberSaving}
            startIcon={memberSaving ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {memberSaving ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default VenueAdminDashboard;